import React from 'react';

import { cssMap } from '@atlaskit/css';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SearchDialogHeaderAnalyticsWrapper } from '../../../utils/quick-find/analytics';
import { onBrowserHistoryHeadingClicked } from '../../../utils/quick-find/events/browser-history-heading-clicked';
import { Heading } from '../heading';

const styles = cssMap({
	headingContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
	},

	rightSection: {
		display: 'flex',
		gap: token('space.075'),
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
	},

	rightText: {
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		'&:hover': {
			color: token('color.text'),
		},
	},
});

type BrowserHistoryHeadingProps = {
	id: string;
	leftText: string;
	rightText: string;
	onRightTextClick: () => void;
	fireAnalyticsEvent: (payload: any) => void;
	commonAttributes: Record<string, any>;
	nonPrivacySafeAttributes: Record<string, any>;
};

export const BrowserHistoryHeading = ({
	id,
	leftText,
	rightText,
	onRightTextClick,
	fireAnalyticsEvent,
	commonAttributes,
	nonPrivacySafeAttributes,
}: React.PropsWithChildren<BrowserHistoryHeadingProps>): React.JSX.Element => {
	const handleRightTextClick = () => {
		fireAnalyticsEvent(
			onBrowserHistoryHeadingClicked({
				attributes: commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
		onRightTextClick();
	};

	const headingContent = (
		<Inline xcss={styles.headingContainer}>
			{/* eslint-disable-next-line @atlassian/a11y/heading-has-content -- See https://go/a11y-heading-has-content for more details */}
			<Heading id={id} text={leftText} includeInAnalytics={false} />
			<Pressable xcss={styles.rightSection} onClick={handleRightTextClick}>
				<InformationCircleIcon size="small" label="history-heading-info-icon" />
				<Box xcss={styles.rightText}>{rightText}</Box>
			</Pressable>
		</Inline>
	);

	return (
		<SearchDialogHeaderAnalyticsWrapper id={id}>
			{headingContent}
		</SearchDialogHeaderAnalyticsWrapper>
	);
};
